import { useMemo, useState } from "react";
import { useConversations } from "./useConversations";
import useDebouncedValue from "./useDebouncedValue";
import useConversationQueueNavigation from "./useConversationQueueNavigation";
import {
  isQueueEligibleConversation,
  loadChatAdvancedFilterState,
  loadChatListFilters,
  loadChatListSearch,
  resolveChatListAdvancedParams,
  resolveChatListApiSearchTerm,
  stripClientOnlyChatFilters,
} from "../pages/Chat/utils/chatListContext";

/**
 * Fila de solicitações para navegação anterior/próxima.
 * No chat principal, passe `conversations` da lista já carregada (mesmos filtros da UI).
 * No popup-call, omita `conversations` para buscar com filtros persistidos.
 */
export default function useChatConversationQueue(
  currentConversationId,
  {
    onNavigate,
    enabled = true,
    conversations: conversationsFromParent,
    fetchNextPage: fetchNextPageFromParent,
    hasNextPage: hasNextPageFromParent,
    isFetchingNextPage: isFetchingNextPageFromParent,
    totalCount: totalCountFromParent,
  } = {}
) {
  const useParentList = Array.isArray(conversationsFromParent);

  const [searchTerm] = useState(loadChatListSearch);
  const [filters] = useState(loadChatListFilters);
  const [advancedFilterState] = useState(loadChatAdvancedFilterState);

  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500);
  const apiSearchTerm = useMemo(
    () => resolveChatListApiSearchTerm(debouncedSearchTerm),
    [debouncedSearchTerm]
  );
  const debouncedFilters = useDebouncedValue(filters, 400);
  const debouncedAdvancedFilterState = useDebouncedValue(advancedFilterState, 400);

  const debouncedFiltersForApi = useMemo(
    () => stripClientOnlyChatFilters(debouncedFilters),
    [debouncedFilters]
  );

  const advancedParams = useMemo(
    () => resolveChatListAdvancedParams(debouncedFilters, debouncedAdvancedFilterState),
    [debouncedFilters, debouncedAdvancedFilterState]
  );

  const internalQuery = useConversations(
    apiSearchTerm,
    debouncedFiltersForApi,
    advancedParams,
    !useParentList
  );

  const conversations = useMemo(() => {
    const list = useParentList
      ? conversationsFromParent
      : internalQuery.data?.pages?.flatMap((page) => page.list) ?? [];
    return list.filter(isQueueEligibleConversation);
  }, [useParentList, conversationsFromParent, internalQuery.data?.pages]);

  const totalInDatabase = useParentList
    ? totalCountFromParent
    : internalQuery.data?.pages?.[0]?.totalInDatabase;

  const fetchNextPage = useParentList ? fetchNextPageFromParent : internalQuery.fetchNextPage;
  const hasNextPage = useParentList ? Boolean(hasNextPageFromParent) : Boolean(internalQuery.hasNextPage);
  const isFetchingNextPage = useParentList
    ? Boolean(isFetchingNextPageFromParent)
    : Boolean(internalQuery.isFetchingNextPage);
  const isLoading = useParentList ? false : internalQuery.isLoading;

  const navigation = useConversationQueueNavigation({
    currentConversationId,
    conversations,
    totalCount: totalInDatabase,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    onNavigate,
    enabled,
  });

  return {
    conversations,
    isLoading,
    totalInDatabase,
    ...navigation,
  };
}
